/**
 * The 3-2-1 countdown screen shown when the host starts a round (server drives
 * the deadline). Number swaps + a GSAP pop per tick (the gold fill is pure CSS;
 * GSAP only scales/fades). Phase 3 wires the subtle beep on each tick.
 *
 * Heuristic: we treat a short BETTING/DEALING deadline at round start as the
 * countdown. Numbers come from the server `deadlineTs` — the client never
 * enforces time, it only displays it.
 */

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { useCountdown } from './useCountdown';
import { useGameStore } from '../state/store';
import { playSfx } from '../audio/sfx';

export function Countdown321() {
  const phase = useGameStore((s) => s.server.phase);
  const deadlineTs = useGameStore((s) => s.server.deadlineTs);
  const { seconds, active } = useCountdown(deadlineTs);

  const numRef = useRef<HTMLDivElement>(null);
  const lastTick = useRef<number>(-1);
  const [display, setDisplay] = useState<number>(seconds);

  // Show the 3-2-1 ONLY in the final seconds of BETTING (the pre-deal beat) — not
  // during DEALING, which would double up and flicker a second "2,1". When all
  // bets are in, the server shortens BETTING to ~3s so the full 3-2-1 plays once.
  const show = active && phase === 'BETTING' && seconds <= 3 && seconds > 0;

  useEffect(() => {
    if (show && seconds !== lastTick.current) {
      lastTick.current = seconds;
      setDisplay(seconds);
      playSfx('countdownBeep');
    }
  }, [show, seconds]);

  useGSAP(
    () => {
      if (show && numRef.current) {
        gsap.fromTo(
          numRef.current,
          { scale: 0.3, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(2)' },
        );
      }
    },
    { dependencies: [display, show] },
  );

  if (!show) return null;

  return (
    <div className="countdown321">
      <div ref={numRef} className="countdown321__num title-gold">
        {display}
      </div>
    </div>
  );
}
